import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  FaStar,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaCalendar,
  FaHiking,
  FaDollarSign,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa'
import tourService from '../services/tourService'
import reviewService from '../services/reviewService'
import { formatPrice } from '../utils/helpers'
import Loader from '../components/Loader'
import SEO from '../utils/SEO'
import { generateTourSchema, generateBreadcrumbs } from '../utils/structuredData'
import { trackViewItem } from '../utils/analytics'
import Button from '../components/ui/Button'
import QuoteModal from '../components/QuoteModal'

const TourDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [tour, setTour] = useState(null)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [reviews, setReviews] = useState([])
  const [statistics, setStatistics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    fetchTourDetails()
    fetchReviews()
  }, [id])

  const fetchTourDetails = async () => {
    try {
      const data = await tourService.getTourById(id)
      setTour(data.tour)
      trackViewItem({
        id: data.tour.id,
        name: data.tour.title || data.tour.name,
        price: data.tour.price,
        category: 'tour',
      })
      setLoading(false)
    } catch (error) {
      toast.error('Failed to load tour details')
      setLoading(false)
    }
  }

  const fetchReviews = async () => {
    try {
      const data = await reviewService.getReviews('tour', id)
      setReviews(data.reviews)
      setStatistics(data.statistics)
    } catch (error) {
      console.error('Failed to load reviews')
    }
  }

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-800',
      moderate: 'bg-yellow-100 text-yellow-800',
      hard: 'bg-red-100 text-red-800',
    }
    return colors[difficulty] || colors.moderate
  }

  const getCategoryBadge = (category) => {
    const badges = {
      standard: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      luxury: 'bg-gold-100 text-gold-800',
    }
    return badges[category] || badges.standard
  }

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={star <= rating ? 'text-yellow-400' : 'text-gray-300'}
            size={16}
          />
        ))}
      </div>
    )
  }

  const nextImage = () => {
    if (tour.images && tour.images.length > 0) {
      setCurrentImageIndex((prev) => (prev === tour.images.length - 1 ? 0 : prev + 1))
    }
  }

  const prevImage = () => {
    if (tour.images && tour.images.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? tour.images.length - 1 : prev - 1))
    }
  }

  const handleBookNow = () => {
    navigate(`/tours/${id}/book`)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={80} />
      </div>
    )
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Tour Not Found</h2>
          <Button onClick={() => navigate('/tours')}>Back to Tours</Button>
        </div>
      </div>
    )
  }

  const images = tour.images
    ? Array.isArray(tour.images)
      ? tour.images
      : JSON.parse(tour.images)
    : []

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <SEO
          title={`${tour.title || tour.name} - Book ${tour.destination || 'Himachal'} Tour`}
          description={`${(tour.description || '').slice(0, 150)}. ${tour.duration || ''} tour starting ${formatPrice(tour.price || 0)}. Book now with Himachal Yatra.`}
          canonical={`/tours/${tour.id}`}
          image={images[0]}
          type="product"
          keywords={`${tour.destination || ''} tour package, ${tour.title || tour.name}, book ${tour.destination || 'Himachal'} tour, Himachal Pradesh tour`}
          structuredData={[
            generateTourSchema(tour),
            generateBreadcrumbs([
              { name: 'Home', path: '/' },
              { name: 'Tours', path: '/tours' },
              { name: tour.title || tour.name, path: `/tours/${tour.id}` },
            ]),
          ]}
        />
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Image Gallery */}
          <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden mb-8 bg-gray-200">
            {images.length > 0 ? (
              <>
                <img
                  src={images[currentImageIndex]}
                  alt={tour.title}
                  className="w-full h-full object-cover"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition"
                    >
                      <FaChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition"
                    >
                      <FaChevronRight size={20} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition ${
                            index === currentImageIndex ? 'bg-white w-8' : 'bg-white/60'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No images available
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2">
              {/* Tour Header */}
              <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{tour.title}</h1>
                    <div className="flex items-center gap-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <FaMapMarkerAlt className="text-blue-600" />
                        <span>{tour.location_name || tour.destination}</span>
                      </div>
                      {tour.average_rating > 0 && (
                        <div className="flex items-center gap-2">
                          <FaStar className="text-yellow-400" />
                          <span className="font-semibold">{tour.average_rating}</span>
                          <span className="text-gray-500">({tour.total_reviews} reviews)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryBadge(tour.category)}`}
                    >
                      {tour.category?.toUpperCase()}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(tour.difficulty)}`}
                    >
                      {tour.difficulty?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <FaClock className="text-blue-600" size={20} />
                    <div>
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-semibold">{tour.duration} Days</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaUsers className="text-blue-600" size={20} />
                    <div>
                      <div className="text-sm text-gray-500">Group Size</div>
                      <div className="font-semibold">Max {tour.max_group_size}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaDollarSign className="text-blue-600" size={20} />
                    <div>
                      <div className="text-sm text-gray-500">Starting From</div>
                      <div className="font-semibold text-blue-600">
                        {tour.location_price_on_request
                          ? 'Price on Request'
                          : formatPrice(tour.base_price)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-xl shadow-sm mb-6">
                <div className="border-b">
                  <div className="flex gap-8 px-6">
                    {['overview', 'itinerary', 'included', 'reviews'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`py-4 font-semibold transition border-b-2 ${
                          activeTab === tab
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-6">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div>
                      <h3 className="text-xl font-bold mb-4">Tour Overview</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {tour.description}
                      </p>
                    </div>
                  )}

                  {/* Itinerary Tab */}
                  {activeTab === 'itinerary' && (
                    <div>
                      <h3 className="text-xl font-bold mb-6">Day-by-Day Itinerary</h3>
                      {tour.itinerary &&
                      Array.isArray(tour.itinerary) &&
                      tour.itinerary.length > 0 ? (
                        <div className="space-y-6">
                          {tour.itinerary.map((day, index) => (
                            <div
                              key={index}
                              className="border-l-4 border-blue-600 pl-6 pb-6 relative"
                            >
                              <div className="absolute left-0 top-0 transform -translate-x-1/2 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                {day.day}
                              </div>
                              <h4 className="text-lg font-bold text-gray-900 mb-2">{day.title}</h4>
                              <div className="mb-3">
                                <h5 className="font-semibold text-gray-700 mb-2">Activities:</h5>
                                <ul className="space-y-1">
                                  {day.activities?.map((activity, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-600">
                                      <FaCheck
                                        className="text-green-500 mt-1 flex-shrink-0"
                                        size={14}
                                      />
                                      <span>{activity}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {day.meals && day.meals.length > 0 && (
                                <div className="mb-2">
                                  <span className="font-semibold text-gray-700">Meals: </span>
                                  <span className="text-gray-600">{day.meals.join(', ')}</span>
                                </div>
                              )}
                              {day.accommodation && (
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    Accommodation:{' '}
                                  </span>
                                  <span className="text-gray-600">{day.accommodation}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">Itinerary details will be provided soon.</p>
                      )}
                    </div>
                  )}

                  {/* Included Tab */}
                  {activeTab === 'included' && (
                    <div>
                      <h3 className="text-xl font-bold mb-4">What's Included</h3>
                      {tour.services && tour.services.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-4">
                          {tour.services.map((service) => (
                            <div
                              key={service.id}
                              className="flex items-start gap-3 p-4 border rounded-lg"
                            >
                              <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                              <div>
                                <h5 className="font-semibold text-gray-900">{service.name}</h5>
                                <p className="text-sm text-gray-600">{service.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">Service details will be provided soon.</p>
                      )}

                      {tour.activities && tour.activities.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-lg font-bold mb-4">Available Activities</h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            {tour.activities.map((activity) => (
                              <div
                                key={activity.id}
                                className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50"
                              >
                                <FaHiking className="text-blue-600 mt-1 flex-shrink-0" />
                                <div>
                                  <h5 className="font-semibold text-gray-900">{activity.name}</h5>
                                  <p className="text-sm text-gray-600">{activity.description}</p>
                                  <p className="text-sm font-semibold text-blue-600 mt-1">
                                    +{formatPrice(activity.price)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reviews Tab */}
                  {activeTab === 'reviews' && (
                    <div>
                      <h3 className="text-xl font-bold mb-6">Customer Reviews</h3>

                      {/* Rating Summary */}
                      {statistics && statistics.total_reviews > 0 && (
                        <div className="bg-gray-50 rounded-lg p-6 mb-6">
                          <div className="flex items-center gap-8">
                            <div className="text-center">
                              <div className="text-4xl font-bold text-gray-900">
                                {statistics.average_rating}
                              </div>
                              {renderStars(Math.round(statistics.average_rating))}
                              <div className="text-sm text-gray-500 mt-1">
                                {statistics.total_reviews} reviews
                              </div>
                            </div>
                            <div className="flex-1">
                              {[5, 4, 3, 2, 1].map((rating) => {
                                const count =
                                  statistics[
                                    `${['one', 'two', 'three', 'four', 'five'][rating - 1]}_star`
                                  ] || 0
                                const percentage =
                                  statistics.total_reviews > 0
                                    ? ((count / statistics.total_reviews) * 100).toFixed(0)
                                    : 0
                                return (
                                  <div key={rating} className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-medium w-8">{rating} ★</span>
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-yellow-400 h-2 rounded-full"
                                        style={{ width: `${percentage}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-sm text-gray-600 w-12 text-right">
                                      {percentage}%
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Reviews List */}
                      {reviews.length > 0 ? (
                        <div className="space-y-4">
                          {reviews.map((review) => (
                            <div key={review.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h5 className="font-semibold text-gray-900">
                                    {review.user_name}
                                  </h5>
                                  {renderStars(review.rating)}
                                </div>
                                <span className="text-sm text-gray-500">
                                  {new Date(review.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              {review.comment && (
                                <p className="text-gray-700 mt-2">{review.comment}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">
                          No reviews yet. Be the first to review this tour!
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Sidebar */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-xl p-6 shadow-lg sticky top-8">
                <div className="mb-6">
                  {tour.location_price_on_request ? (
                    <>
                      <div className="text-xl font-bold text-amber-600 mb-1">Price on Request</div>
                      <div className="text-sm text-gray-500">Contact us for a custom quote</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-gray-500 mb-1">Price starts from</div>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatPrice(tour.base_price)}
                      </div>
                      <div className="text-sm text-gray-500">per person</div>
                    </>
                  )}
                </div>

                {!tour.location_price_on_request && (
                  <Button onClick={handleBookNow} fullWidth className="mb-4">
                    Reserve Your Spot
                  </Button>
                )}

                <button
                  onClick={() => setShowQuoteModal(true)}
                  className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-lg transition mb-4"
                >
                  Request Free Quote
                </button>

                <div className="border-t pt-4 space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaCheck className="text-green-500" />
                    <span>Free cancellation up to 48 hours</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaCheck className="text-green-500" />
                    <span>Instant confirmation</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaCheck className="text-green-500" />
                    <span>24/7 customer support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        tourTitle={tour?.title}
      />
    </>
  )
}

export default TourDetails
